import { ORDER_LOG_EVENT } from '@/constants/logs/order';
import {
  usePharmacyAdditionalOrderCategories,
  usePharmacyAdditionalOrderProductsByPharmacyIdAndCategoryIdInfinite,
  useRecommendedPharmacyAdditionalOrderProducts,
} from '@/entities/additional-order';
import {
  AdditionalOrderCategoryModel,
  AdditionalOrderProductModel,
} from '@/entities/additional-order/model/AdditionalOrder';
import { useRequestPartnershipPickupDispensation } from '@/entities/order';
import useLogger from '@/hooks/useLogger';
import useModal from '@/hooks/useModal';
import useNavigate from '@/hooks/useNavigate';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { EDeliveryCategoryType, EPrescriptionSendType } from '@/types/pharmacy';
import { historyBack } from '@/utils/mobile/RNPlugins';
import { nativePostMessageEventList } from '@/utils/mobile/postMessageEventList.messages';
import { warningReport } from '@/utils/report';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AdditionalOrderProductWithCount {
  quantity: number;
  id: string;
  categoryId: string;
  name: string;
  manufacturer: string;
  imageUrl: string;
  price: number;
}

interface RequestPartnershipPickupDispensationProps {
  products: AdditionalOrderProductWithCount[];
  pharmacyId: string;
  diagnosisId: string;
  mainTagTypes: string;
  isUserAlternativeSelected: string | null;
  medicineRequestInfos: string;
}

export interface AdditionalOrderViewModel {
  recommendedDescription: string;
  isRecommendedAdditionalOrderProductLoading: boolean;
  additionalOrderCategories: AdditionalOrderCategoryModel[];
  isLoadingAdditionalOrderProducts: boolean;
  fetchNextPageAdditionalOrderProducts: () => void;
  hasNextPageAdditionalOrderProducts: boolean;
  selectedCategoryId: string | null;
  additionalOrderProducts?: AdditionalOrderProductModel[];
  selectedProducts: AdditionalOrderProductWithCount[];
  onClickCategoryTab: (category: AdditionalOrderCategoryModel) => void;
  getIsSelectedProduct: (product: AdditionalOrderProductModel) => boolean;
  onAddProductToCart: (product: AdditionalOrderProductModel) => void;
  onRemoveProductFromCart: (product: AdditionalOrderProductModel) => void;
  getSelectedProductCount: (product: AdditionalOrderProductModel) => number;
  onSkipToAdditionalOrder: () => void;
  onSubmitOrder: () => void;
}
const DEBOUNCE_TIME = 1500;

export const MAX_ADD_PRODUCT_QUANTITY = 10;
const useAdditionalOrderViewModel = (): AdditionalOrderViewModel => {
  const router = useRouter();
  const { log } = useLogger();
  const navigate = useNavigate();
  const { showModal } = useModal();
  const {
    prescriptionSendType,
    pharmacyId,
    diagnosisId,
    amountToPay,
    mainTagTypes,
    isUserAlternativeSelected = null,
    medicineRequestInfos,
  } = router.query;

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<AdditionalOrderProductWithCount[]>([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string[]>([]);
  const previousCategoryIdRef = useRef<string | null>(null);

  const { recommendedAdditionalOrderProducts, recommendedDescription, isRecommendedAdditionalOrderProductLoading } =
    useRecommendedPharmacyAdditionalOrderProducts(String(diagnosisId), String(pharmacyId));

  const { additionalOrderCategories } = usePharmacyAdditionalOrderCategories();

  const isRecommendedCategorySelected = selectedCategoryId === null && recommendedAdditionalOrderProducts.length > 0;

  const {
    additionalOrderProducts,
    isLoadingAdditionalOrderProducts,
    fetchNextPageAdditionalOrderProducts,
    hasNextPageAdditionalOrderProducts,
  } = usePharmacyAdditionalOrderProductsByPharmacyIdAndCategoryIdInfinite(
    {
      pharmacyId: String(pharmacyId),
      categoryId: selectedCategoryId,
    },
    !isRecommendedCategorySelected
  );

  /* 연관상품 유무에 따른 카테고리 노출 변경 */
  const additionalOrderCategoryList = useMemo(() => {
    if (recommendedAdditionalOrderProducts.length > 0) {
      return additionalOrderCategories?.getCategoriesWithRecommended() ?? [];
    }
    return additionalOrderCategories?.getCategoriesWithAll() ?? [];
  }, [recommendedAdditionalOrderProducts, additionalOrderCategories]);

  /* 연관 상품 유무에 따른 프로덕트 참조값 변경 */
  const products = useMemo(() => {
    if (selectedCategoryId === null && recommendedAdditionalOrderProducts.length > 0) {
      return recommendedAdditionalOrderProducts;
    }
    return additionalOrderProducts;
  }, [recommendedAdditionalOrderProducts, additionalOrderProducts, selectedCategoryId]);

  const { requestPartnershipPickupDispensationMutation } = useRequestPartnershipPickupDispensation();

  const onSetSelectedCategoryName = (categoryName: string) => {
    if (selectedCategoryName.includes(categoryName)) {
      return;
    }
    setSelectedCategoryName((prev) => [...prev, categoryName]);
  };

  const onLogSelectedCategoriesName = () => {
    log({
      eventName: ORDER_LOG_EVENT.CLICKED_ADDITIONAL_ORDER_CATEGORIES,
      payload: {
        categoryName: selectedCategoryName.join(','),
        productsCount: additionalOrderProducts?.length ?? 0,
        pharmacyId: String(pharmacyId),
        diagnosisId: String(diagnosisId),
      },
    });
  };

  const onClickCategoryTab = (category: AdditionalOrderCategoryModel) => {
    log({
      eventName: ORDER_LOG_EVENT.CLICKED_ADDITIONAL_ORDER_CATEGORY_TAB,
      payload: {
        categoryId: category.id ?? '',
        categoryName: category.name,
        previousCategoryId: previousCategoryIdRef.current ?? '',
        pharmacyId: String(pharmacyId),
        diagnosisId: String(diagnosisId),
      },
    });

    previousCategoryIdRef.current = category.id;
    setSelectedCategoryId(category.id);
    onSetSelectedCategoryName(category.name);
  };

  const getIsSelectedProduct = (product: AdditionalOrderProductModel) => {
    return selectedProducts.some((p) => p.id === product.id);
  };

  const getSelectedProductCount = (product: AdditionalOrderProductModel) => {
    return selectedProducts.find((p) => p.id === product.id)?.quantity ?? 0;
  };

  const onAddProductToCart = (product: AdditionalOrderProductModel) => {
    const currentQuantity = getSelectedProductCount(product);
    const isNewProduct = !getIsSelectedProduct(product);
    const newQuantity = isNewProduct ? 1 : currentQuantity + 1;

    log({
      eventName: ORDER_LOG_EVENT.CLICKED_ADDITIONAL_ORDER_ADD_TO_CART,
      payload: {
        productId: product.id,
        productName: product.name,
        categoryId: product.categoryId,
        price: product.price,
        quantity: newQuantity,
        totalSelectedCount: isNewProduct ? selectedProducts.length + 1 : selectedProducts.length,
        pharmacyId: String(pharmacyId),
        diagnosisId: String(diagnosisId),
      },
    });

    if (isNewProduct) {
      setSelectedProducts((prev) => [...prev, { ...product, quantity: newQuantity }]);
      return;
    }
    if (currentQuantity < MAX_ADD_PRODUCT_QUANTITY) {
      setSelectedProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, quantity: newQuantity } : p)));
    }
  };

  const onRemoveProductFromCart = (product: AdditionalOrderProductModel) => {
    const currentQuantity = getSelectedProductCount(product);
    const newQuantity = currentQuantity - 1;
    const willRemove = currentQuantity === 1;

    log({
      eventName: ORDER_LOG_EVENT.CLICKED_ADDITIONAL_ORDER_REMOVE_FROM_CART,
      payload: {
        productId: product.id,
        productName: product.name,
        categoryId: product.categoryId,
        price: product.price,
        quantity: Math.max(0, newQuantity),
        totalSelectedCount: willRemove ? selectedProducts.length - 1 : selectedProducts.length,
        pharmacyId: String(pharmacyId),
        diagnosisId: String(diagnosisId),
      },
    });

    setSelectedProducts((prev) => {
      if (getIsSelectedProduct(product) && currentQuantity > 1) {
        return prev.map((p) => (p.id === product.id ? { ...p, quantity: newQuantity } : p));
      } else if (willRemove) {
        return prev.filter((p) => p.id !== product.id);
      }
      return prev;
    });
  };

  const onSkipToAdditionalOrder = async () => {
    log({
      eventName: ORDER_LOG_EVENT.CLICKED_SKIP_ADDITIONAL_ORDER,
      payload: {
        productsCount: additionalOrderProducts?.length ?? 0,
        pharmacyId: String(pharmacyId),
        diagnosisId: String(diagnosisId),
      },
    });
    onLogSelectedCategoriesName();
    setSelectedProducts([]);
    await onSubmitOrder([]);
  };

  const getUserLocationPosition = async () => {
    let location = {
      longitude: 0,
      latitude: 0,
    };

    try {
      const permission = await nativePostMessageEventList.checkLocationPermission();
      if (permission) {
        const position = await nativePostMessageEventList.getLocationPosition();
        location = { ...position };
      }
      return location;
    } catch {
      warningReport('약국 처방전 전송 페이지에서 위치정보를 가져오는데 실패했습니다.', {});
      return location;
    }
  };

  const onRequestPartnershipPickupDispensation = useCallback(
    debounce(
      async (props: RequestPartnershipPickupDispensationProps) => {
        const receiptLocation = await getUserLocationPosition();
        requestPartnershipPickupDispensationMutation(
          {
            request: {
              pharmacyId: String(props.pharmacyId),
              diagnosisId: String(props.diagnosisId),
              mainTagTypes: JSON.parse(props.mainTagTypes as string),
              ...(props.isUserAlternativeSelected !== null && {
                isUserAlternativeSelected: props.isUserAlternativeSelected === 'true' ? true : false,
              }),
              medicineRequestInfos: JSON.parse(props.medicineRequestInfos as string),
              receiptLocation,
              additionalProducts: !!props.products.length
                ? props.products.map((product) => ({
                    productId: product.id,
                    quantity: product.quantity,
                  }))
                : [],
            },
          },
          {
            onSuccess: () => {
              navigate.navigateToSelectedScreenWithResetNavigation({
                screenName: ROUTES.DIAGNOSIS.DETAIL.appScreenName,
              });
            },
            onError: () => {
              showModal({
                type: MODAL_TYPE.ALERT,
                props: {
                  title: '약국에서 처방전을 받을 수 없어요.',
                  message: '다른 약국을 선택해주세요.',
                  buttons: [
                    {
                      type: MODAL_BUTTON_TYPE.PRIMARY,
                      label: '약국 재선택',
                      onClick: historyBack,
                    },
                  ],
                },
              });
            },
          }
        );
      },
      DEBOUNCE_TIME,
      {
        leading: true,
        trailing: false,
      }
    ),
    []
  );

  const onMovePaymentScreen = (products: AdditionalOrderProductWithCount[]) => {
    navigate.navigateToPaymentScreen({
      price: Number(amountToPay),
      immediatePayOrderInfoV2: {
        diagnosisId: String(diagnosisId),
        pharmacyId: String(pharmacyId),
        amountToPay: Number(amountToPay),
        medicinePriceInfos: JSON.parse(medicineRequestInfos as string),
        deliveryCategoryType: EDeliveryCategoryType.Partnership_pickup,
        mainTagTypes: JSON.parse(mainTagTypes as string).join(','),
        ...(isUserAlternativeSelected !== null && {
          isUserAlternativeSelected: isUserAlternativeSelected === 'true' ? true : false,
        }),
      },
      additionalOrderProducts: {
        additionalProducts: products.length
          ? products.map((product) => ({
              productId: product.id,
              quantity: product.quantity,
              price: product.price,
              name: product.name,
            }))
          : [],
        additionalOrderAmountToPay: products.length
          ? products.reduce((acc, product) => acc + product.price * product.quantity, 0)
          : null,
      },
    });
  };
  const onSubmitOrder = async (products?: AdditionalOrderProductWithCount[]) => {
    onLogSelectedCategoriesName();
    const additionalProducts = products ?? selectedProducts;

    log({
      eventName: ORDER_LOG_EVENT.CLICKED_SUBMIT_ADDITIONAL_ORDER,
      payload: {
        productCount: additionalProducts.length,
        totalAmount: additionalProducts.reduce((acc, product) => acc + product.price * product.quantity, 0),
        productIds: additionalProducts.map((p) => p.id).join(','),
        pharmacyId: String(pharmacyId),
        diagnosisId: String(diagnosisId),
      },
    });

    if (prescriptionSendType === EPrescriptionSendType.ImmediatePaymentPartnership) {
      onMovePaymentScreen(additionalProducts);
      return;
    }
    onRequestPartnershipPickupDispensation({
      products: additionalProducts,
      pharmacyId: String(pharmacyId),
      diagnosisId: String(diagnosisId),
      mainTagTypes: String(mainTagTypes),
      isUserAlternativeSelected: isUserAlternativeSelected !== null ? String(isUserAlternativeSelected) : null,
      medicineRequestInfos: String(medicineRequestInfos),
    });
  };

  useEffect(() => {
    const enterTime = Date.now();

    return () => {
      const exitTime = Date.now();
      const dwellTimeInSeconds = Math.round((exitTime - enterTime) / 1000);

      log({
        eventName: ORDER_LOG_EVENT.EXITED_ADDITIONAL_ORDER_PAGE,
        payload: {
          dwellTime: dwellTimeInSeconds,
          pharmacyId: String(pharmacyId),
          diagnosisId: String(diagnosisId),
        },
      });
    };
  }, []);

  return {
    recommendedDescription,
    isRecommendedAdditionalOrderProductLoading,
    additionalOrderCategories: additionalOrderCategoryList,
    isLoadingAdditionalOrderProducts,
    hasNextPageAdditionalOrderProducts,
    selectedCategoryId,
    additionalOrderProducts: products,
    selectedProducts,
    fetchNextPageAdditionalOrderProducts,
    onClickCategoryTab,
    getIsSelectedProduct,
    onAddProductToCart,
    onRemoveProductFromCart,
    getSelectedProductCount,
    onSkipToAdditionalOrder,
    onSubmitOrder,
  };
};

export default useAdditionalOrderViewModel;
